import { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Plus, Edit, Trash2, Loader2, Eye } from "lucide-react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { Label } from "../../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../components/ui/tabs";
import { Switch } from "../../components/ui/switch";
import { toast } from "sonner";
import {
  faqCategoriesAPI,
  faqQuestionsAPI,
  type FAQCategory,
  type FAQCategoryCreatePayload,
  type FAQQuestion,
  type FAQQuestionCreatePayload,
} from "../../services/api";

export default function FAQManagement() {
  const [categories, setCategories] = useState<FAQCategory[]>([]);
  const [questions, setQuestions] = useState<FAQQuestion[]>([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [questionsLoading, setQuestionsLoading] = useState(true);

  // FAQ Category state
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [categoryForm, setCategoryForm] = useState<FAQCategoryCreatePayload>({
    title: "",
    is_enabled: true,
    order_by: 0,
  });
  const [categorySubmitLoading, setCategorySubmitLoading] = useState(false);
  const [editingCategory, setEditingCategory] = useState<FAQCategory | null>(
    null
  );
  const [deletingCategory, setDeletingCategory] = useState<FAQCategory | null>(
    null
  );
  const [deleteCategoryLoading, setDeleteCategoryLoading] = useState(false);

  // FAQ Question state
  const [questionDialogOpen, setQuestionDialogOpen] = useState(false);
  const [questionForm, setQuestionForm] = useState<FAQQuestionCreatePayload>({
    category: "",
    question: "",
    answer: "",
    is_enabled: true,
    order_by: 0,
  });
  const [questionSubmitLoading, setQuestionSubmitLoading] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState<FAQQuestion | null>(
    null
  );
  const [viewingQuestion, setViewingQuestion] = useState<FAQQuestion | null>(
    null
  );
  const [deletingQuestion, setDeletingQuestion] = useState<FAQQuestion | null>(
    null
  );
  const [deleteQuestionLoading, setDeleteQuestionLoading] = useState(false);

  const fetchCategories = async () => {
    setCategoriesLoading(true);
    try {
      const list = await faqCategoriesAPI.list();
      setCategories(Array.isArray(list) ? list : []);
    } catch {
      toast.error("Failed to load FAQ categories");
      setCategories([]);
    } finally {
      setCategoriesLoading(false);
    }
  };

  const fetchQuestions = async () => {
    setQuestionsLoading(true);
    try {
      const list = await faqQuestionsAPI.list();
      setQuestions(Array.isArray(list) ? list : []);
    } catch {
      toast.error("Failed to load FAQ questions");
      setQuestions([]);
    } finally {
      setQuestionsLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
    fetchQuestions();
  }, []);

  const resetCategoryForm = () => {
    setCategoryForm({ title: "", is_enabled: true, order_by: 0 });
    setEditingCategory(null);
  };

  const resetQuestionForm = () => {
    setQuestionForm({
      category: categories[0]?.id?.toString() ?? "",
      question: "",
      answer: "",
      is_enabled: true,
      order_by: 0,
    });
    setEditingQuestion(null);
  };

  // Category Handlers
  const handleOpenCreateCategory = () => {
    resetCategoryForm();
    setCategoryDialogOpen(true);
  };

  const handleOpenEditCategory = (cat: FAQCategory) => {
    setEditingCategory(cat);
    setCategoryForm({
      title: cat.title,
      is_enabled: cat.is_enabled,
      order_by: cat.order_by,
    });
    setCategoryDialogOpen(true);
  };

  const handleSaveCategory = async () => {
    if (!categoryForm.title.trim()) {
      toast.error("Category title is required");
      return;
    }
    setCategorySubmitLoading(true);
    try {
      if (editingCategory) {
        await faqCategoriesAPI.update(editingCategory.id, categoryForm);
        toast.success("Category updated successfully");
      } else {
        await faqCategoriesAPI.create(categoryForm);
        toast.success("Category created successfully");
      }
      setCategoryDialogOpen(false);
      resetCategoryForm();
      fetchCategories();
    } catch (error: any) {
      toast.error(error?.message ?? "Failed to save category");
    } finally {
      setCategorySubmitLoading(false);
    }
  };

  const handleDeleteCategory = async () => {
    if (!deletingCategory) {
      return;
    }
    setDeleteCategoryLoading(true);
    try {
      await faqCategoriesAPI.delete(deletingCategory.id);
      toast.success("Category deleted successfully");
      setDeletingCategory(null);
      fetchCategories();
    } catch (error: any) {
      toast.error(error?.message ?? "Failed to delete category");
    } finally {
      setDeleteCategoryLoading(false);
    }
  };

  // Question Handlers
  const handleOpenCreateQuestion = () => {
    resetQuestionForm();
    setQuestionDialogOpen(true);
  };

  const handleOpenEditQuestion = (q: FAQQuestion) => {
    setEditingQuestion(q);
    const catId = typeof q.category === "object" ? q.category.id : q.category;
    setQuestionForm({
      category: String(catId),
      question: q.question,
      answer: q.answer,
      is_enabled: q.is_enabled,
      order_by: q.order_by,
    });
    setQuestionDialogOpen(true);
  };

  const handleSaveQuestion = async () => {
    if (!questionForm.question.trim()) {
      toast.error("Question is required");
      return;
    }

    // Strip HTML tags to check if answer is effectively empty
    const answerText = questionForm.answer.replace(/<[^>]*>/g, "").trim();
    if (!answerText) {
      toast.error("Answer is required");
      return;
    }

    if (!questionForm.category) {
      toast.error("Please select a category");
      return;
    }
    setQuestionSubmitLoading(true);
    try {
      const payload = {
        ...questionForm,
        category: Number(questionForm.category) || questionForm.category,
      };
      if (editingQuestion) {
        await faqQuestionsAPI.update(editingQuestion.id, payload);
        toast.success("Question updated successfully");
      } else {
        await faqQuestionsAPI.create(payload);
        toast.success("Question created successfully");
      }
      setQuestionDialogOpen(false);
      resetQuestionForm();
      fetchQuestions();
    } catch (error: any) {
      toast.error(error?.message ?? "Failed to save question");
    } finally {
      setQuestionSubmitLoading(false);
    }
  };

  const handleDeleteQuestion = async () => {
    if (!deletingQuestion) {
      return;
    }
    setDeleteQuestionLoading(true);
    try {
      await faqQuestionsAPI.delete(deletingQuestion.id);
      toast.success("Question deleted successfully");
      setDeletingQuestion(null);
      fetchQuestions();
    } catch (error: any) {
      toast.error(error?.message ?? "Failed to delete question");
    } finally {
      setDeleteQuestionLoading(false);
    }
  };

  const getCategoryTitle = (q: FAQQuestion) => {
    if (q.category_name) {
      return q.category_name;
    }
    const catId = typeof q.category === "object" ? q.category.id : q.category;
    const cat = categories.find(c => String(c.id) === String(catId));
    return cat?.title ?? "—";
  };

  return (
    <div className="space-y-6 p-6">
      <div>
        <h1 className="text-3xl font-bold text-foreground">FAQ Management</h1>
        <p className="text-muted-foreground">
          Manage frequently asked questions and categories
        </p>
      </div>

      <Tabs defaultValue="categories" className="space-y-4">
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="categories">FAQ Categories</TabsTrigger>
          <TabsTrigger value="questions">FAQ Questions</TabsTrigger>
        </TabsList>

        <TabsContent value="categories" className="space-y-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0">
              <CardTitle>Categories ({categories.length})</CardTitle>
              <Button onClick={handleOpenCreateCategory} className="gap-2">
                <Plus className="h-4 w-4" />
                Create new category
              </Button>
            </CardHeader>
            <CardContent>
              {categoriesLoading ? (
                <div className="flex justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              ) : categories.length === 0 ? (
                <p className="py-8 text-center text-muted-foreground">
                  No categories yet.
                </p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Order</TableHead>
                      <TableHead>Title</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="w-[120px]">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {categories.map(cat => (
                      <TableRow key={cat.id}>
                        <TableCell>{cat.order_by}</TableCell>
                        <TableCell className="font-medium">
                          {cat.title}
                        </TableCell>
                        <TableCell>
                          <span
                            className={
                              cat.is_enabled
                                ? "text-green-600"
                                : "text-muted-foreground"
                            }
                          >
                            {cat.is_enabled ? "Enabled" : "Disabled"}
                          </span>
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleOpenEditCategory(cat)}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setDeletingCategory(cat)}
                              className="text-destructive hover:text-destructive"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="questions" className="space-y-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0">
              <CardTitle>Questions ({questions.length})</CardTitle>
              <Button
                onClick={handleOpenCreateQuestion}
                className="gap-2"
                disabled={categories.length === 0}
              >
                <Plus className="h-4 w-4" />
                Create new question
              </Button>
            </CardHeader>
            <CardContent>
              {questionsLoading ? (
                <div className="flex justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              ) : questions.length === 0 ? (
                <p className="py-8 text-center text-muted-foreground">
                  No questions yet.
                </p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Order</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead>Question</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="w-[120px]">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {questions.map(q => (
                      <TableRow key={q.id}>
                        <TableCell>{q.order_by}</TableCell>
                        <TableCell>{getCategoryTitle(q)}</TableCell>
                        <TableCell className="max-w-[300px] truncate font-medium">
                          {q.question}
                        </TableCell>
                        <TableCell>
                          <span
                            className={
                              q.is_enabled
                                ? "text-green-600"
                                : "text-muted-foreground"
                            }
                          >
                            {q.is_enabled ? "Enabled" : "Disabled"}
                          </span>
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setViewingQuestion(q)}
                              className="text-blue-600 hover:text-blue-700"
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleOpenEditQuestion(q)}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setDeletingQuestion(q)}
                              className="text-destructive hover:text-destructive"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Category Dialog */}
      <Dialog open={categoryDialogOpen} onOpenChange={setCategoryDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingCategory
                ? "Edit FAQ Category"
                : "Create New FAQ Category"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div className="space-y-2">
              <Label htmlFor="cat-title">Title *</Label>
              <Input
                id="cat-title"
                value={categoryForm.title}
                onChange={e =>
                  setCategoryForm({ ...categoryForm, title: e.target.value })
                }
                placeholder="Category title"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="cat-order">Order</Label>
              <Input
                id="cat-order"
                type="number"
                value={categoryForm.order_by}
                onChange={e =>
                  setCategoryForm({
                    ...categoryForm,
                    order_by: Number(e.target.value),
                  })
                }
              />
            </div>
            <div className="flex items-center gap-2">
              <Switch
                id="cat-enabled"
                checked={categoryForm.is_enabled}
                onCheckedChange={checked =>
                  setCategoryForm({ ...categoryForm, is_enabled: checked })
                }
              />
              <Label htmlFor="cat-enabled">Enabled</Label>
            </div>
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setCategoryDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveCategory}
                disabled={categorySubmitLoading}
              >
                {categorySubmitLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : editingCategory ? (
                  "Update"
                ) : (
                  "Create"
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Question Dialog */}
      <Dialog open={questionDialogOpen} onOpenChange={setQuestionDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {editingQuestion
                ? "Edit FAQ Question"
                : "Create New FAQ Question"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div className="space-y-2">
              <Label htmlFor="q-category">Category *</Label>
              <Select
                value={String(questionForm.category)}
                onValueChange={v =>
                  setQuestionForm({ ...questionForm, category: v })
                }
              >
                <SelectTrigger id="q-category">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map(c => (
                    <SelectItem key={c.id} value={String(c.id)}>
                      {c.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="q-text">Question *</Label>
              <Input
                id="q-text"
                value={questionForm.question}
                onChange={e =>
                  setQuestionForm({ ...questionForm, question: e.target.value })
                }
                placeholder="Enter question"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="q-answer">Answer *</Label>
              <div className="rounded-md">
                <ReactQuill
                  theme="snow"
                  value={questionForm.answer}
                  onChange={content =>
                    setQuestionForm({ ...questionForm, answer: content })
                  }
                  placeholder="Enter detailed answer here..."
                  modules={{
                    toolbar: [
                      [{ header: [1, 2, false] }],
                      ["bold", "italic", "underline", "strike", "blockquote"],
                      [{ list: "ordered" }, { list: "bullet" }],
                      ["link", "clean"],
                    ],
                  }}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="q-order">Order</Label>
                <Input
                  id="q-order"
                  type="number"
                  value={questionForm.order_by}
                  onChange={e =>
                    setQuestionForm({
                      ...questionForm,
                      order_by: Number(e.target.value),
                    })
                  }
                />
              </div>
              <div className="flex items-center gap-2 pt-8">
                <Switch
                  id="q-enabled"
                  checked={questionForm.is_enabled}
                  onCheckedChange={checked =>
                    setQuestionForm({ ...questionForm, is_enabled: checked })
                  }
                />
                <Label htmlFor="q-enabled">Enabled</Label>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setQuestionDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveQuestion}
                disabled={questionSubmitLoading}
              >
                {questionSubmitLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : editingQuestion ? (
                  "Update"
                ) : (
                  "Create"
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Category Confirmation */}
      <Dialog
        open={!!deletingCategory}
        onOpenChange={open => !open && setDeletingCategory(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete FAQ Category</DialogTitle>
          </DialogHeader>
          <p className="py-4 text-muted-foreground">
            Are you sure you want to delete category &quot;
            <strong>{deletingCategory?.title}</strong>&quot;? This will not
            delete questions in this category but they will lose their category
            association.
          </p>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setDeletingCategory(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteCategory}
              disabled={deleteCategoryLoading}
            >
              {deleteCategoryLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                "Delete"
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* View Question Dialog */}
      <Dialog
        open={!!viewingQuestion}
        onOpenChange={open => !open && setViewingQuestion(null)}
      >
        <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>FAQ Details</DialogTitle>
          </DialogHeader>
          <div className="space-y-6 pt-4">
            <div className="grid grid-cols-2 gap-4 border-b pb-4">
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                  Category
                </Label>
                <p className="mt-1 font-medium">
                  {viewingQuestion ? getCategoryTitle(viewingQuestion) : "—"}
                </p>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                  Status
                </Label>
                <div className="mt-1">
                  <span
                    className={
                      viewingQuestion?.is_enabled
                        ? "font-medium text-green-600"
                        : "font-medium text-muted-foreground"
                    }
                  >
                    {viewingQuestion?.is_enabled ? "Enabled" : "Disabled"}
                  </span>
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                Question
              </Label>
              <p className="text-lg font-semibold leading-relaxed">
                {viewingQuestion?.question}
              </p>
            </div>

            <div className="mt-4 space-y-2 border-t pt-2">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                Answer
              </Label>
              <div
                className="prose prose-slate mt-2 max-w-none text-sm leading-relaxed"
                dangerouslySetInnerHTML={{
                  __html: viewingQuestion?.answer ?? "",
                }}
              />
            </div>

            <div className="flex justify-end pt-4">
              <Button
                variant="outline"
                onClick={() => setViewingQuestion(null)}
              >
                Close
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Question Confirmation */}
      <Dialog
        open={!!deletingQuestion}
        onOpenChange={open => !open && setDeletingQuestion(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete FAQ Question</DialogTitle>
          </DialogHeader>
          <p className="py-4 text-muted-foreground">
            Are you sure you want to delete this question?
          </p>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setDeletingQuestion(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteQuestion}
              disabled={deleteQuestionLoading}
            >
              {deleteQuestionLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                "Delete"
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      {/* Rich Text Editor Styles */}
      <style>{`
                .ql-container {
                    border-bottom-left-radius: 0.5rem;
                    border-bottom-right-radius: 0.5rem;
                    background-color: transparent;
                    font-family: inherit;
                    min-height: 150px;
                }
                .ql-toolbar {
                    border-top-left-radius: 0.5rem;
                    border-top-right-radius: 0.5rem;
                    background-color: var(--muted);
                    border-color: var(--input) !important;
                }
                .ql-container.ql-snow {
                    border-color: var(--input) !important;
                }
                .ql-editor {
                    font-size: 0.875rem;
                    line-height: 1.5;
                }
                .ql-editor.ql-blank::before {
                    color: var(--muted-foreground);
                    font-style: normal;
                    font-size: 0.875rem;
                }
            `}</style>
    </div>
  );
}
